import { CompanyInfo, formatVnd, lineAmounts, priceText, QuoteData, summarize } from './calc';

/**
 * Vẽ báo giá thành ảnh PNG bằng Canvas 2D (không thêm thư viện): bố cục giống bảng tính gốc của KTV —
 * dải tiêu đề, bảng theo nhóm có màu nền, tổng tiền, phần chia Công ty / Creator, lưu ý.
 */
const W = 1200;
const PAD = 40;
const FONT = '"Be Vietnam Pro", "Segoe UI", Arial, sans-serif';
const CATEGORY_COLORS = ['#E3E3E3', '#FCE5CD', '#E8C6D8', '#D9EAD3', '#CFE2F3'];
const COLS = [
  { key: 'name', label: 'TÊN THIẾT BỊ', w: 520, align: 'left' as const },
  { key: 'price', label: 'ĐƠN GIÁ', w: 150, align: 'right' as const },
  { key: 'qty', label: 'SL', w: 60, align: 'center' as const },
  { key: 'amount', label: 'THÀNH TIỀN', w: 160, align: 'right' as const },
  { key: 'pct', label: 'HỖ TRỢ', w: 90, align: 'center' as const },
  { key: 'creator', label: 'CREATOR TRẢ', w: 140, align: 'right' as const },
];

function fit(ctx: CanvasRenderingContext2D, text: string, max: number) {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
}

function wrap(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    let line = '';
    for (const word of para.split(' ')) {
      const next = line ? `${line} ${word}` : word;
      if (ctx.measureText(next).width > max && line) {
        out.push(line);
        line = word;
      } else line = next;
    }
    if (line) out.push(line);
  }
  return out;
}

export async function renderQuoteImage(opts: {
  code: string;
  data: QuoteData;
  company: CompanyInfo;
  date?: Date;
  /** Ảnh sản phẩm đã nạp (imageKey → ảnh); thiếu thì dòng không có ảnh. */
  thumbs?: Map<string, CanvasImageSource>;
}): Promise<Blob> {
  const { data, company } = opts;
  const thumbs = opts.thumbs ?? new Map<string, CanvasImageSource>();
  try {
    await (document as any).fonts?.ready;
  } catch {
    // trình duyệt cũ: dùng font dự phòng
  }
  const s = summarize(data);
  const categories = Array.from(new Set(data.lines.map((l) => l.category)));
  // Có ảnh sản phẩm thì dòng cao hơn để ảnh 48×48 không bị bóp.
  const ROW = thumbs.size ? 58 : 40;
  const THUMB = 48;
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = `16px ${FONT}`;
  const noteLines = data.notes ? wrap(measure, data.notes, W - PAD * 2) : [];
  measure.font = `14px ${FONT}`;
  const contactLines = wrap(
    measure,
    [
      company.name && `${company.name}${company.taxCode ? ` · MST ${company.taxCode}` : ''}`,
      company.officeAddress && `Văn phòng: ${company.officeAddress}`,
      company.phone && `Điện thoại: ${company.phone}`,
      company.fanpage && `Fanpage: ${company.fanpage}`,
      company.tiktok && `TikTok: ${company.tiktok}`,
    ]
      .filter(Boolean)
      .join('\n'),
    W - PAD * 2
  );
  const height =
    150 +
    48 +
    categories.length * 46 +
    data.lines.length * ROW +
    30 +
    70 +
    6 * 34 +
    (noteLines.length ? 30 + noteLines.length * 24 : 0) +
    (contactLines.length ? 40 + contactLines.length * 22 : 0) +
    50;

  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = W * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, height);
  ctx.textBaseline = 'middle';

  // Dải tiêu đề
  const grad = ctx.createLinearGradient(0, 0, W, 0);
  grad.addColorStop(0, '#1B2150');
  grad.addColorStop(1, '#3A3F8F');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, 110);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `700 30px ${FONT}`;
  ctx.fillText('BÁO GIÁ THIẾT BỊ LIVESTREAM', PAD, 42);
  ctx.font = `500 16px ${FONT}`;
  // Trên TikTok LIVE công ty dùng tên thương hiệu (shortName) — đặt to; tên pháp nhân in ở chân ảnh.
  ctx.fillText(company.shortName || company.name || 'HP Media', PAD, 80);
  ctx.textAlign = 'right';
  ctx.font = `700 18px ${FONT}`;
  ctx.fillText(opts.code, W - PAD, 42);
  ctx.font = `400 15px ${FONT}`;
  ctx.fillText((opts.date ?? new Date()).toLocaleDateString('vi-VN'), W - PAD, 80);
  ctx.textAlign = 'left';

  let y = 130;
  ctx.fillStyle = '#1B2150';
  ctx.font = `600 17px ${FONT}`;
  const who = [
    data.creator.fullName && `Creator: ${data.creator.fullName}`,
    data.creator.tiktokId && `TikTok: @${data.creator.tiktokId.replace(/^@/, '')}`,
    data.creator.phone && `SĐT: ${data.creator.phone}`,
  ]
    .filter(Boolean)
    .join('   ·   ');
  ctx.fillText(fit(ctx, who || 'Báo giá tham khảo', W - PAD * 2), PAD, y);
  y += 30;

  // Đầu bảng
  const tableW = COLS.reduce((a, c) => a + c.w, 0);
  const x0 = (W - tableW) / 2;
  ctx.fillStyle = '#A4C2F4';
  ctx.fillRect(x0, y, tableW, 44);
  ctx.fillStyle = '#111111';
  ctx.font = `700 14px ${FONT}`;
  let x = x0;
  for (const c of COLS) {
    ctx.textAlign = c.align;
    const tx = c.align === 'left' ? x + 12 : c.align === 'right' ? x + c.w - 12 : x + c.w / 2;
    ctx.fillText(c.label, tx, y + 22);
    x += c.w;
  }
  y += 48;

  categories.forEach((category, ci) => {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1B2150';
    ctx.font = `700 20px ${FONT}`;
    ctx.fillText(`${ci + 1}. ${category.toUpperCase()}`, W / 2, y + 22);
    y += 46;
    for (const line of data.lines.filter((l) => l.category === category)) {
      const a = lineAmounts(line);
      ctx.fillStyle = CATEGORY_COLORS[ci % CATEGORY_COLORS.length];
      ctx.fillRect(x0, y, tableW, ROW - 2);
      ctx.fillStyle = '#222222';
      ctx.font = `400 16px ${FONT}`;
      const thumb = line.imageKey ? thumbs.get(line.imageKey) : undefined;
      const cells: Record<string, string> = {
        name: line.condition === 'USED' ? `${line.name} (đã qua sử dụng)` : line.name,
        price: priceText(line),
        qty: String(line.qty),
        amount: formatVnd(a.amount),
        pct: line.advance
          ? 'Ứng trước'
          : line.included && data.hpCreator
          ? 'Tặng kèm'
          : line.supportPercent
          ? `${line.supportPercent}%`
          : '—',
        creator: formatVnd(a.creator),
      };
      x = x0;
      for (const c of COLS) {
        ctx.textAlign = c.align;
        let tx = c.align === 'left' ? x + 12 : c.align === 'right' ? x + c.w - 12 : x + c.w / 2;
        let maxW = c.w - 20;
        if (c.key === 'name' && thumbs.size) {
          if (thumb) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(x + 8, y + (ROW - 2 - THUMB) / 2, THUMB, THUMB);
            ctx.drawImage(thumb, x + 8, y + (ROW - 2 - THUMB) / 2, THUMB, THUMB);
            ctx.fillStyle = '#222222';
          }
          tx += THUMB + 8;
          maxW -= THUMB + 8;
        }
        ctx.fillText(fit(ctx, cells[c.key], maxW), tx, y + ROW / 2);
        x += c.w;
      }
      y += ROW;
    }
  });

  // Tổng
  y += 20;
  ctx.strokeStyle = '#1B2150';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x0, y);
  ctx.lineTo(x0 + tableW, y);
  ctx.stroke();
  y += 36;
  ctx.textAlign = 'left';
  ctx.fillStyle = '#1B2150';
  ctx.font = `800 26px ${FONT}`;
  ctx.fillText('CHI PHÍ TỔNG', x0 + 12, y);
  ctx.textAlign = 'right';
  ctx.fillText(formatVnd(s.total), x0 + tableW - 12, y);
  y += 44;

  const rows: [string, string, string?][] = [
    [`Công ty hỗ trợ (${s.companyRate}%)`, formatVnd(s.company), '#1E7B4F'],
    ['Creator thanh toán', formatVnd(s.creator), '#C2410C'],
  ];
  if (s.upfront) rows.push(['• Trả trước khi nhận thiết bị', formatVnd(s.upfront)]);
  if (s.monthly)
    rows.push([
      `• Trả dần ${data.payment.installmentMonths} tháng, mỗi tháng`,
      formatVnd(s.monthly),
    ]);
  if (s.travelSupport)
    rows.push(['Chi phí đi lại / lắp đặt Công ty hỗ trợ', formatVnd(s.travelSupport)]);
  for (const [label, value, color] of rows.slice(0, 6)) {
    ctx.font = `${color ? 700 : 400} 18px ${FONT}`;
    ctx.fillStyle = color ?? '#333333';
    ctx.textAlign = 'left';
    ctx.fillText(label, x0 + 12, y);
    ctx.textAlign = 'right';
    ctx.fillText(value, x0 + tableW - 12, y);
    y += 34;
  }

  if (noteLines.length) {
    y += 16;
    ctx.textAlign = 'left';
    ctx.font = `400 16px ${FONT}`;
    noteLines.forEach((line, i) => {
      ctx.fillStyle = i === 0 ? '#C00000' : '#444444';
      ctx.fillText(i === 0 ? `Lưu ý: ${line}` : line, PAD, y);
      y += 24;
    });
  }

  if (contactLines.length) {
    y += 18;
    ctx.fillStyle = '#E5E7F0';
    ctx.fillRect(PAD, y, W - PAD * 2, 1);
    y += 22;
    ctx.textAlign = 'left';
    ctx.font = `400 14px ${FONT}`;
    ctx.fillStyle = '#555B7A';
    for (const line of contactLines) {
      ctx.fillText(line, PAD, y);
      y += 22;
    }
  }

  // Chiều cao ước lượng trước luôn dư một chút → cắt ảnh vừa khít nội dung.
  const used = Math.min(height, Math.ceil(y + 30));
  let out = canvas;
  if (used < height) {
    out = document.createElement('canvas');
    out.width = W * scale;
    out.height = used * scale;
    out.getContext('2d')?.drawImage(canvas, 0, 0);
  }
  return new Promise((resolve, reject) =>
    out.toBlob((b) => (b ? resolve(b) : reject(new Error('Không tạo được ảnh'))), 'image/png')
  );
}
